import React from 'react';

import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useNavigate } from 'react-router';

import CircleIcon from '@mui/icons-material/Circle';
import { InvoiceFragment } from '../generated/graphql';
import { appPaths } from '../app-paths';

interface InvoiceDataGridProps {
  invoices: InvoiceFragment[];
  formatNumber: (value: number, decimals: number) => string;
  formatDate: (value: string) => string;
  onRowClick?: (invoiceId: string) => void;
  showContractColumn?: boolean;
  pageSize?: number;
}

export function InvoiceDataGrid({
  invoices,
  formatNumber,
  formatDate,
  onRowClick,
  showContractColumn = true,
  pageSize = 25,
}: InvoiceDataGridProps) {
  const navigate = useNavigate();

  const columns = React.useMemo((): GridColDef<InvoiceFragment>[] => {
    const cols: GridColDef<InvoiceFragment>[] = [
      {
        field: 'paidAt',
        headerName: '',
        type: 'string',

        renderCell: (params) => {
          if (params.row.isDraft) {
            return <CircleIcon sx={{ color: 'grey.400', fontSize: 'small' }} />;
          }
          if (params.value) {
            return null;
          }

          const now = Date.now();
          const dueDate = new Date(params.row.dueDate).getTime();

          return <CircleIcon sx={{ color: dueDate < now ? 'red' : 'orange', fontSize: 'small' }} />;
        },
        width: 50,
        disableColumnMenu: true,
        sortable: false,
      },
      {
        field: 'invoiceNo',
        headerName: 'Číslo',
        width: 100,
        valueFormatter: (value: string | null | undefined) => value || '—',
      },
      {
        field: 'contract',
        headerName: 'Prenájom',
        width: 250,
        valueGetter: (_value, row) => row.contract?.title || '',
      },
      {
        field: 'totalAmount',
        headerName: 'Celkom',
        width: 120,
        valueFormatter: (value: number) => formatNumber(value, 2) + ' €',
      },
      {
        field: 'issuedAt',
        headerName: 'Vystavená',
        type: 'string',
        valueFormatter: (value: string | null | undefined) => (value ? formatDate(value) : '—'),
        width: 130,
      },
      {
        field: 'dueDate',
        headerName: 'Splatná',
        type: 'string',
        valueFormatter: (value: string) => formatDate(value),
        width: 130,
      },

      {
        field: 'description',
        headerName: 'Popis',
        width: 300,
        flex: 1,
      },
    ];
    if (!showContractColumn) {
      cols.splice(2, 1);
    }
    return cols;
  }, [formatNumber, formatDate, navigate]);

  return (
    <DataGrid
      rows={invoices}
      columns={columns}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: pageSize,
          },
        },
        sorting: {
          sortModel: [{ field: 'invoiceNo', sort: 'desc' }],
        },
      }}
      pageSizeOptions={[10, 25, 50, 100]}
      disableRowSelectionOnClick
      onRowClick={(params) => onRowClick && onRowClick(params.row.id)}
      sx={{
        '& .MuiDataGrid-row': {
          cursor: 'pointer',
        },
      }}
    />
  );
}
